/** CES / voice assistant WebSocket tool and message type literals (single source of truth). */

export const VOICE_BOT_TOOL_TYPES = {
  GET_PAGE_INFORMATION: 'get_page_information',
  GET_PLAN_DETAILS: 'get_plan_details',
};

export const VOICE_BOT_MESSAGE_TYPES = {
  ADD_TO_CART: 'add_to_cart',
  SELECT_PLAN: 'select_plan',
  SELECT_PLAN_ALIAS: 'plan',
  SELECT_PERK: 'select_perk',
  PERKS: 'perks',
  NAVIGATE: 'navigate',
  /** PDP browse / CES alternate navigation tool name */
  NAVIGATE_TO_PDP_SCREEN: 'navigate_to_pdp_screen',
};

export const VOICE_BOT_MESSAGE_VALUES = {
  ADD_TO_CART: 'addtocart',
};

export const VOICE_BOT_PAGE_INFO_CATEGORY = {
  PLAN: 'plan',
  PLANS: 'plans',
  PERK: 'perk',
  PERKS: 'perks',
  ACCESSORY: 'accessory',
  ACCESSORIES: 'accessories',
  /** Express checkout / cart Q&A */
  CART: 'cart',
  CARTS: 'carts',
  ITEM: 'item',
  ITEMS: 'items',
  ORDER: 'order',
};

/** Read Gemini / protobuf struct string (string or `{ stringValue }`). */
export function readProtoStringLike(node) {
  if (node == null) return '';
  if (typeof node === 'string') return node.trim();
  if (typeof node === 'object' && node.stringValue != null) return String(node.stringValue).trim();
  return '';
}

/**
 * ADK `function_call` often sets `message.type` to `select_plan` / `select_perk` without `data.fields.type`.
 * Pull a primary string argument from common field names.
 */
export function extractToolCallPrimaryArgString(message) {
  const fields = message?.data?.fields || {};
  const keys = [
    'value',
    'planName',
    'plan_name',
    'name',
    'plan',
    'displayName',
    'perkName',
    'perk_name',
    'perk',
  ];
  for (const key of keys) {
    const s = readProtoStringLike(fields[key]);
    if (s) return s;
  }
  return '';
}

/**
 * When CES sends function_call, merge top-level `type` into command parsing if inner `fields.type` is absent.
 * @returns {{ messageType: string, messageValue: string, messageListValues: string[] }}
 */
export function applyFunctionCallActionFallbacks(message, parsed) {
  let { messageType, messageValue } = parsed;
  const { messageListValues } = parsed;
  const isFunctionCall = message?.type_of_request === 'function_call';
  if (!isFunctionCall) {
    return { messageType, messageValue, messageListValues };
  }

  const topType = String(message?.type ?? '').toLowerCase().trim();
  const inferTypeFromTop = new Set([
    VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN,
    VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN_ALIAS,
    VOICE_BOT_MESSAGE_TYPES.SELECT_PERK,
    VOICE_BOT_MESSAGE_TYPES.PERKS,
    VOICE_BOT_MESSAGE_TYPES.ADD_TO_CART,
  ]);

  if (!messageType && topType && inferTypeFromTop.has(topType)) {
    messageType = topType;
  }

  const needsValue =
    messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN ||
    messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN_ALIAS ||
    messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PERK ||
    messageType === VOICE_BOT_MESSAGE_TYPES.PERKS;

  if (needsValue && !String(messageValue || '').trim() && (!messageListValues || messageListValues.length === 0)) {
    const alt = extractToolCallPrimaryArgString(message);
    if (alt) messageValue = alt;
  }

  return { messageType, messageValue, messageListValues };
}

/**
 * Proto list_value → string[] (same pattern as NGD Browse usePdpVoiceBotCallback).
 */
export function extractVoiceBotSocketListValues(message) {
  const raw = message?.data?.fields?.value?.listValue?.values;
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => v?.stringValue).filter((s) => s != null && String(s).length > 0);
}

/**
 * Normalize command fields from CES function_call / MVA payloads (proto fields vs plain JSON).
 * @returns {{ messageType: string, messageValue: string, messageListValues: string[] }}
 */
export function parseVoiceBotSocketCommand(message) {
  let messageType = message?.data?.fields?.type?.stringValue;
  let messageValue = message?.data?.fields?.value?.stringValue;
  const messageListValues = extractVoiceBotSocketListValues(message);

  if (messageValue == null && message?.data?.value != null && typeof message.data.value !== 'object') {
    messageValue = message.data.value;
  }
  if (!messageType && message?.data?.type != null) {
    messageType = message.data.type;
  }

  const directType = typeof message?.type === 'string' ? message.type.toLowerCase() : '';
  const commandTypes = new Set([
    VOICE_BOT_MESSAGE_TYPES.ADD_TO_CART,
    VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN,
    VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN_ALIAS,
    VOICE_BOT_MESSAGE_TYPES.SELECT_PERK,
    VOICE_BOT_MESSAGE_TYPES.PERKS,
    VOICE_BOT_MESSAGE_TYPES.NAVIGATE,
    VOICE_BOT_MESSAGE_TYPES.NAVIGATE_TO_PDP_SCREEN,
  ]);
  if (!messageType && commandTypes.has(directType)) {
    messageType = message.type;
  }

  return {
    messageType: messageType != null ? String(messageType).toLowerCase().trim() : '',
    messageValue: messageValue != null ? String(messageValue) : '',
    messageListValues,
  };
}
